/**
 * Feb 28, 2026 · LineHistoryDrawer
 *
 * Right-hand drawer that renders a per-line audit timeline: line edits,
 * short closes, and every excess-dispatch entry linked to the SO/ESO line.
 * Consumes the read endpoint `GET /api/line-history` and sorts events
 * newest-first client-side.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ClockCounterClockwiseIcon,
  PencilSimpleIcon,
  ProhibitIcon,
  PackageIcon,
} from "@phosphor-icons/react";

const CLOSE_T = ["short-close", "order-short-close"];
const REV_T = ["reverse-short-close", "reverse-order-short-close"];

const iconFor = (t) =>
  CLOSE_T.includes(t) || t === "customer-cancel-packed" ? <ProhibitIcon size={12} weight="bold" /> :
  t === "excess-dispatch" ? <PackageIcon size={12} weight="bold" /> :
  <PencilSimpleIcon size={12} weight="bold" />;

const badgeFor = (t) =>
  CLOSE_T.includes(t) ? "bg-red-500/15 text-red-300 border-red-500/40" :
  t === "customer-cancel-packed" ? "bg-orange-500/15 text-orange-300 border-orange-500/40" :
  REV_T.includes(t) ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
  t === "excess-dispatch" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" :
  "bg-sky-500/15 text-sky-300 border-sky-500/40";

const LABELS = {
  "short-close": "Short Close", "excess-dispatch": "Excess Dispatch", "adjust-qty": "Qty Adjusted",
  "customer-cancel-packed": "Customer Cancel (Packed)", "reverse-short-close": "Short Close Reversed",
  "order-short-close": "Order Short Close", "reverse-order-short-close": "Order Short Close Reversed",
};
const labelFor = (t) => LABELS[t] || t;

const Q = ({ v, cls = "text-white" }) => <span className={`font-mono ${cls}`}>{v === null || v === undefined ? "—" : fmtQty(v)}</span>;

// P2-E-DEF-03 · recorded quantities only; never derived.
function EventDetail({ r }) {
  if (r.type === "customer-cancel-packed") return (
    <>Unpacked <Q v={r.unpacked_qty} cls="text-red-300" /> · Packed <Q v={r.packed_qty} cls="text-orange-300" /> · Boxes{" "}
      <span className="font-mono" data-testid="lh-boxes">{Array.isArray(r.boxes) ? r.boxes.length : "—"}</span></>);
  if (CLOSE_T.includes(r.type)) return (
    <>Closed <Q v={r.closed_qty} cls="text-red-300" />
      {r.reserved_packed_qty !== null && r.reserved_packed_qty !== undefined && <> · Reserved packed <Q v={r.reserved_packed_qty} /></>}</>);
  if (REV_T.includes(r.type)) return <>Restored <Q v={r.restored_qty} cls="text-emerald-300" /></>;
  return null;
}

const fmtQty = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

// P2-E-DEF-02 · read-only Quantity Ledger (Administrator-only API; hidden when not permitted)
function LedgerSection({ ledger }) {
  return (
    <div className="mb-4" data-testid="line-history-ledger">
      <div className="text-[10px] uppercase tracking-widest text-[var(--accent)] mb-1.5">Quantity Ledger · {ledger.length}</div>
      {ledger.length === 0 ? (
        <div className="text-xs text-zinc-500 italic" data-testid="line-history-ledger-empty">No quantity-ledger entries on this line.</div>
      ) : (
        <ol className="space-y-2 max-h-[30vh] overflow-y-auto pr-1">
          {ledger.map((x) => {
            const refs = [x.customer_po_ref && `PO ${x.customer_po_ref}`, x.customer_instruction_ref && `Instr ${x.customer_instruction_ref}`,
              x.fg_return_no && `FG Return ${x.fg_return_no}`, (x.boxes || []).length ? `${x.boxes.length} box(es)` : null,
              x.reversal_of && `Reverses ${String(x.reversal_of).slice(0, 8)}`].filter(Boolean);
            return (
              <li key={x.entry_id} className="border border-[var(--border)] rounded-sm px-2 py-1.5" data-testid={`line-history-ledger-${x.entry_id}`}>
                <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                  <span className="px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 uppercase tracking-widest" data-testid="ledger-op">{x.op}</span>
                  <span className="text-white text-xs" data-testid="ledger-qty">{fmtQty(x.qty)} m</span>
                  <span className="text-zinc-500">{(x.at || "").slice(0, 19).replace("T", " ")}</span>
                  <span className="text-zinc-400" data-testid="ledger-by">· {x.by_email || "—"}</span>
                  {x.reversed_by && <span className="px-1 rounded-sm border border-red-500/40 text-red-300 uppercase" data-testid="ledger-reversed">Reversed</span>}
                </div>
                {x.reason && <div className="mt-1 text-[11px] text-zinc-400 italic" data-testid="ledger-reason">"{x.reason}"</div>}
                {(x.before || x.after) && (
                  <div className="mt-0.5 text-[10px] text-zinc-500 font-mono" data-testid="ledger-before-after">
                    Effective {fmtQty(x.before?.E)} → {fmtQty(x.after?.E)} · Pending {fmtQty(x.before?.pending)} → {fmtQty(x.after?.pending)}
                    {x.initiator ? ` · ${x.initiator}` : ""}{x.reversed_by ? ` · Reversed by ${String(x.reversed_by).slice(0, 8)}` : ""}
                  </div>
                )}
                {refs.length > 0 && <div className="mt-0.5 text-[10px] text-zinc-500 font-mono" data-testid="ledger-refs">{refs.join(" · ")}</div>}
              </li>
            );
          })}
        </ol>
      )}
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">Line Audit History</div>
    </div>
  );
}

export default function LineHistoryDrawer({ open, onClose, source, orderId, lineId, lineRef, orderNo, productCode }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    setMeta({});
    api.get("/line-history", { params: { source, order_id: orderId, line_id: lineId || "", line_ref: lineRef || "" } })
      .then(({ data }) => { setRows((data && data.events) || []); setMeta({ ambiguous: data && data.excess_attribution === "AMBIGUOUS" }); })
      .catch((e) => { setRows([]); const d = e?.response?.data?.detail; setMeta({ error: (d && (d.message || d)) || "Line history could not be loaded" }); })
      .finally(() => setLoading(false));
  }, [open, source, orderId, lineId, lineRef]);

  useEffect(() => {
    setLedger(null);
    if (!open || !orderId || !lineRef || !source) return;
    api.get(`/orders/${source.toLowerCase()}/${orderId}/lines/${encodeURIComponent(lineRef)}/qty-ledger`)
      .then(({ data }) => setLedger((data && data.qty_ledger) || []))
      .catch(() => setLedger(null));
  }, [open, source, orderId, lineRef]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-xl" data-testid="line-history-drawer" description="Full change history for the selected order line with before / after diffs.">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ClockCounterClockwiseIcon size={16} weight="bold" />
            Line History
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] font-mono text-zinc-400 mb-2">
          {source} · <span className="text-amber-300">{orderNo || "—"}</span>
          {productCode && <> · <span className="text-zinc-200 truncate">{productCode}</span></>}
        </div>
        {ledger && <LedgerSection ledger={ledger} />}
        {meta.ambiguous && (
          <div className="mb-2 text-[11px] text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-sm px-2 py-1" data-testid="line-history-excess-ambiguous">
            Some excess-production entries cannot be reliably attributed to this line and are not shown.
          </div>
        )}
        {loading ? (
          <div className="text-xs text-zinc-500 py-8 text-center">Loading history…</div>
        ) : meta.error ? (
          <div className="text-xs text-red-300 py-6 text-center" data-testid="line-history-error">{String(meta.error)}</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-zinc-500 py-8 text-center italic" data-testid="line-history-empty">
            No audit events on this line yet.
          </div>
        ) : (
          <ol className="relative border-l border-[var(--border)] ml-2 pl-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <li key={i} className="relative" data-testid={`line-history-event-${i}`}>
                <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full flex items-center justify-center border ${badgeFor(r.type)}`}>{iconFor(r.type)}</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${badgeFor(r.type)}`}>
                    {labelFor(r.type)}
                  </span>
                  <span className="text-[10px] text-zinc-500 font-mono">{r.at}</span>
                  {r.by && <span className="text-[10px] text-zinc-500">· {r.by}</span>}
                </div>
                <div className="mt-1 text-xs text-zinc-300 leading-snug">
                  {r.type === "adjust-qty" && (
                    <>Qty <span className="font-mono">{r.from_qty}</span> → <span className="font-mono text-white">{r.to_qty}</span></>
                  )}
                  {r.type === "short-close" && (
                    <>Cancelled pending <Q v={r.closed_qty} cls="text-red-300" />
                      {r.reserved_packed_qty !== null && r.reserved_packed_qty !== undefined && <> · Reserved packed <Q v={r.reserved_packed_qty} /></>}</>
                  )}
                  {r.type !== "short-close" && <EventDetail r={r} />}
                  {r.type === "excess-dispatch" && (
                    <>Excess <span className="font-mono text-amber-300">{r.excess_qty}</span> {r.unit || "m"}
                      {r.entry_no && <> · <span className="font-mono text-zinc-400">{r.entry_no}</span></>}
                      {r.status && <> · <span className={`font-mono ${r.status === "Approved for Billing" ? "text-emerald-300" : "text-zinc-400"}`}>{r.status}</span></>}
                      {r.slip_no && r.slip_no !== "MANUAL" && <> · <span className="font-mono text-zinc-500">{r.slip_no}</span></>}
                    </>
                  )}
                </div>
                {r.reason && <div className="mt-1 text-[11px] text-zinc-400 italic">"{r.reason}"</div>}
                {r.ledger_entry_id && <div className="mt-0.5 text-[10px] text-zinc-500 font-mono" data-testid={`line-history-event-${i}-ledger`}>Ledger {String(r.ledger_entry_id).slice(0, 8)}</div>}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
